import { useSyncExternalStore } from 'react'

export type AlumniTheme = 'dark' | 'light'

const STORAGE_KEY = 'alumni-theme'
const THEME_EVENT = 'alumni-theme-change'

export function getStoredTheme(): AlumniTheme | null {
	try {
		const v = localStorage.getItem(STORAGE_KEY)
		return v === 'dark' || v === 'light' ? v : null
	} catch {
		return null
	}
}

export function resolveTheme(): AlumniTheme {
	const stored = globalThis.window === undefined ? null : getStoredTheme()
	if (stored) return stored
	// Default: mantener dark (consistente con el UI actual).
	// Si quieres respetar el SO, cambia a `return prefersDark() ? 'dark' : 'light'`.
	if (globalThis.window !== undefined) return 'dark'
	return 'dark'
}

export function applyTheme(theme: AlumniTheme): void {
	if (typeof document === 'undefined') return
	document.documentElement.classList.toggle('dark', theme === 'dark')
	try {
		localStorage.setItem(STORAGE_KEY, theme)
	} catch {
		/* ignore */
	}
	try {
		globalThis.window.dispatchEvent(new Event(THEME_EVENT))
	} catch {
		/* ignore */
	}
}

export function subscribeTheme(onStoreChange: () => void): () => void {
	if (globalThis.window === undefined)
		return () => {
			/* ignore */
		}

	const onEvent = () => onStoreChange()
	globalThis.window.addEventListener(THEME_EVENT, onEvent)

	// Cambios desde otras pestañas.
	const onStorage = (e: StorageEvent) => {
		if (e.key === STORAGE_KEY) onStoreChange()
	}
	globalThis.window.addEventListener('storage', onStorage)

	// Cambios directos al classList (por seguridad, por si algo toca el DOM).
	const el = document.documentElement
	const observer = new MutationObserver(onStoreChange)
	observer.observe(el, { attributes: true, attributeFilter: ['class'] })

	return () => {
		globalThis.window.removeEventListener(THEME_EVENT, onEvent)
		globalThis.window.removeEventListener('storage', onStorage)
		observer.disconnect()
	}
}

export function getThemeSnapshot(): AlumniTheme {
	if (typeof document === 'undefined') return 'dark'
	return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

export function getThemeServerSnapshot(): AlumniTheme {
	return 'dark'
}

export function useAlumniTheme(): { theme: AlumniTheme; isDark: boolean; setTheme: (t: AlumniTheme) => void; toggle: () => void } {
	const theme = useSyncExternalStore(subscribeTheme, getThemeSnapshot, getThemeServerSnapshot)
	return {
		theme,
		isDark: theme === 'dark',
		setTheme: applyTheme,
		toggle: () => applyTheme(theme === 'dark' ? 'light' : 'dark')
	}
}
